import type { CustomField, LeadTag, RichLead } from '../../lead.popup/types';
import type { AdditionalContact, Lead } from '../../types';

import { cache } from '../cache';
import { estClient } from './estClient';
import { NotFoundError } from './httpClient';

export interface LeadStatus {
  id: string;
  name: string;
  order: string;
}

interface LeadUpdateProps extends Omit<RichLead, 'objects' | 'tags'> {
  // objects and tags can be in simplified format that is generated on frontend side
  objects:
    | RichLead['objects']
    | {
        object_id: string;
        object: {
          id: string;
          name: string;
        };
      }[];
  tags:
    | RichLead['tags']
    | {
        tag_id: string;
        tag: {
          id: string;
          name: string;
        };
      }[];

  /**
   * Additional prop when updating lead segmentation
   */
  segmentation_objects?: { [id: string]: string }[];

  /**
   * Additional prop when updating lead segmentation
   */
  segmentation_tags?: { [id: string]: string }[];
}

export interface NewLeadProps {
  name: string;
  objects: { [id: string]: string }[];
  tags: { [id: string]: string }[];
  note: string;
  phone: string;
  email: string;
  status_id: string;
  custom_fields: unknown[];
  contacts: { value: string }[];
  custom_note: string;
  ad_source_id: string | null;
}

interface MergeResult {
  success: boolean;
  message: string;
  lead: RichLead;
}

class LeadApi {
  async getAll({
    fields,
    ...query
  }: {
    limit?: number;
    offset?: number;
    query?: string;
    fields?: string[];
    order_by?: string;
    order?: 'desc' | 'asc';
  }): Promise<{ rows: Lead[]; total: string }> {
    return estClient.get<{ rows: Lead[]; total: string }>(`/api/lead/all`, {
      query: {
        ...query,
        'fields[]': fields,
      },
    });
  }

  async get(leadId: string): Promise<RichLead | null> {
    try {
      return estClient.get<RichLead>(`/api/lead/get/${leadId}`);
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async create(lead: NewLeadProps): Promise<RichLead> {
    return estClient.post<RichLead>(`/api/lead/create_complete`, {
      body: lead,
    });
  }

  async update(leadId: string, lead: LeadUpdateProps): Promise<void> {
    return estClient.post<void>(`/api/lead/${leadId}/update`, {
      body: lead,
    });
  }

  async getStatuses(): Promise<LeadStatus[]> {
    return cache.get('lead-statuses', () =>
      estClient.get<LeadStatus[]>('/api/lead/status'),
    );
  }

  async getTags(): Promise<LeadTag[]> {
    return cache.get('lead-tags', () =>
      estClient.get<LeadTag[]>('/api/lead/tags'),
    );
  }

  async getCustomFields(): Promise<CustomField[]> {
    return cache.get('lead-customFields', () =>
      estClient.get<CustomField[]>('/api/lead/custom_fields'),
    );
  }

  async createAdditionalContact(
    leadId: string,
    contact: AdditionalContact,
  ): Promise<AdditionalContact> {
    return estClient.post<AdditionalContact>(
      `/api/lead/${leadId}/create_additional`,
      {
        body: {
          additional_contacts: contact,
        },
      },
    );
  }

  async updateAdditionalContact(
    leadId: string,
    { contact, index }: { contact: AdditionalContact; index: number },
  ): Promise<void> {
    return estClient.post<void>(`/api/lead/${leadId}/update_additional`, {
      body: {
        additional_contacts: contact,
        additional_id: index,
      },
    });
  }

  async merge({
    sourceId,
    destinationId,
    contactType,
  }: {
    sourceId: string;
    destinationId: string;
    contactType: 'main' | 'additional';
  }): Promise<MergeResult> {
    const destinationType = { main: 1, additional: 0 }[contactType];

    return estClient.get<MergeResult>(
      `/api/lead/merge/${sourceId}/${destinationId}/${destinationType}`,
    );
  }
}

export const leadApi = new LeadApi();
